import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const { 
  FiZap, FiArrowRight, FiShield, FiClipboard
} = FiIcons;
const { FaWhatsapp } = FaIcons;

const QuoteCalculator = ({ logoUrl }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    machineType: 'centro-lavoro',
    serviceType: 'riparazione',
    urgency: 'normale',
  });

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);

  const whatsappLink = `https://wa.me/393498408946?text=${encodeURIComponent(
    `Richiesta Preventivo Mars Systems\n` +
    `- Macchina: ${formData.machineType}\n` +
    `- Servizio: ${formData.serviceType}\n` +
    `- Urgenza: ${formData.urgency}\n` +
    `Contattatemi per un preventivo dettagliato.`
  )}`;

  return (
    <div className="min-h-screen bg-slate-50 pt-28 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
        >
          <div className="bg-slate-900 p-10 text-white text-center">
            <div className="mb-6 flex justify-center">
              {/* Il logo diventa bianco sullo sfondo scuro */}
              <img src={logoUrl} alt="Mars Systems" className="h-24 object-contain brightness-0 invert" />
            </div>
            <h1 className="text-3xl font-bold">Richiesta Preventivo</h1>
            <p className="text-slate-400 mt-2 uppercase tracking-widest text-xs font-bold">CNC Technical Support</p>
          </div>

          <div className="p-8">
            <div className="flex items-center justify-between mb-12 relative max-w-md mx-auto">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex flex-col items-center z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= s ? 'bg-blue-600 text-white scale-110 shadow-lg shadow-blue-500/30' : 'bg-slate-200 text-slate-400'
                  }`}>
                    {s}
                  </div>
                </div>
              ))}
              <div className="absolute top-5 left-0 w-full h-0.5 bg-slate-100 -z-0">
                <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${((step - 1) / 2) * 100}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="st1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wider">Tipo Macchinario</label>
                      <select 
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.machineType}
                        onChange={(e) => setFormData({...formData, machineType: e.target.value})}
                      >
                        <option value="centro-lavoro">Centro di Lavoro</option>
                        <option value="tornio">Tornio CNC</option>
                        <option value="fresa">Fresatrice CNC</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wider">Tipo Intervento</label>
                      <select 
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.serviceType}
                        onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                      >
                        <option value="ordinaria">Manutenzione Ordinaria</option>
                        <option value="riparazione">Riparazione Guasto</option>
                        <option value="installazione">Installazione / Trasferimento</option>
                        <option value="elettronica">Assistenza Elettronica/PLC</option>
                      </select>
                    </div>
                  </div>
                  <button onClick={handleNext} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-blue-600 transition-all flex items-center justify-center gap-2 shadow-lg">
                    Prossimo Step <SafeIcon icon={FiArrowRight} />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="st2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div 
                      onClick={() => setFormData({...formData, urgency: 'normale'})}
                      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${formData.urgency === 'normale' ? 'border-blue-600 bg-blue-50 shadow-inner' : 'border-slate-100 hover:border-slate-300'}`}
                    >
                      <SafeIcon icon={FiClipboard} className="text-2xl mb-2 text-blue-600" />
                      <h3 className="font-bold text-slate-900">Intervento Standard</h3>
                      <p className="text-sm text-slate-500">Pianificato entro 5 giorni lavorativi</p>
                    </div>
                    <div 
                      onClick={() => setFormData({...formData, urgency: 'pronto-intervento'})}
                      className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${formData.urgency === 'pronto-intervento' ? 'border-red-600 bg-red-50 shadow-inner' : 'border-slate-100 hover:border-slate-300'}`}
                    >
                      <SafeIcon icon={FiZap} className="text-2xl mb-2 text-red-600" />
                      <h3 className="font-bold text-slate-900">Pronto Intervento</h3>
                      <p className="text-sm text-slate-500">Uscita garantita entro 24 ore</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={handleBack} className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-xl font-bold">Indietro</button>
                    <button onClick={handleNext} className="flex-[2] py-4 bg-blue-600 text-white rounded-xl font-bold shadow-lg">Conferma Dati</button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="st3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-8">
                  <div className="bg-slate-50 rounded-3xl p-10 border border-slate-200">
                    <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-500/20">
                      <SafeIcon icon={FiShield} className="text-4xl" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">Ottimo! I dati sono pronti.</h2>
                    <p className="text-slate-600 text-lg">Invia la tua richiesta ora per ricevere assistenza immediata dal nostro team tecnico.</p>
                  </div>
                  <div className="flex flex-col gap-4">
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full py-5 bg-green-500 text-white rounded-2xl font-bold text-xl hover:bg-green-600 transition-all flex items-center justify-center gap-3 shadow-xl shadow-green-500/20">
                      <SafeIcon icon={FaWhatsapp} /> Richiedi Preventivo via WhatsApp
                    </a>
                    <button onClick={() => setStep(1)} className="text-blue-600 font-bold hover:underline">Modifica dati</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QuoteCalculator;